import { __assign } from "tslib";
import { clamp } from '../../../libs/utils';
export var DateComponent;
(function (DateComponent) {
    DateComponent[DateComponent["dayPeriod"] = 1] = "dayPeriod";
    DateComponent[DateComponent["second"] = 2] = "second";
    DateComponent[DateComponent["minute"] = 4] = "minute";
    DateComponent[DateComponent["hour"] = 8] = "hour";
    DateComponent[DateComponent["day"] = 16] = "day";
    DateComponent[DateComponent["month"] = 32] = "month";
    DateComponent[DateComponent["year"] = 64] = "year";
})(DateComponent || (DateComponent = {}));
var MIN_VALUES = {
    second: 0,
    minute: 0,
    hour: 0,
    day: 1,
    month: 1,
    year: 1,
};
var MAX_VALUES = {
    second: 59,
    minute: 59,
    hour: 23,
    day: 31,
    month: 12,
    year: 9999,
};
var DateComponents = /** @class */ (function () {
    function DateComponents(components) {
        if (components === void 0) { components = {}; }
        this.components = {
            day: null,
            hour: null,
            minute: null,
            second: null,
            month: null,
            year: null,
        };
        this.filledComponentsMask = 0;
        this.set('day', components.day);
        this.set('hour', components.hour);
        this.set('dayPeriod', components.dayPeriod);
        this.set('minute', components.minute);
        this.set('second', components.second);
        this.set('month', components.month);
        this.set('year', components.year);
    }
    DateComponents.from = function (date) {
        if (date === void 0) { date = null; }
        if (date === null) {
            return new DateComponents();
        }
        var value = new Date(date);
        var hour = value.getHours();
        return new DateComponents({
            dayPeriod: hour < 12 ? 1 : 2,
            second: value.getSeconds(),
            minute: value.getMinutes(),
            hour: hour,
            day: value.getDate(),
            month: value.getMonth() + 1,
            year: value.getFullYear(),
        });
    };
    DateComponents.isSame = function (type, a, b) {
        switch (type) {
            case 'year':
                return isSameYear(a, b);
            case 'month':
                return isSameMonth(a, b);
            case 'day':
                return isSameDay(a, b);
            case 'hour':
                return isSameHour(a, b);
            case 'minute':
                return isSameMinute(a, b);
            case 'second':
                return isSameSecond(a, b);
            case 'dayPeriod':
                return isSameDay(a, b) && isSameDateComponent('dayPeriod', a, b);
        }
    };
    DateComponents.isEqual = function (a, b) {
        return isSameSecond(a, b);
    };
    DateComponents.isDateComponentType = function (type) {
        return type in DateComponent;
    };
    DateComponents.prototype.get = function (type, defaultValue) {
        var _a, _b;
        switch (type) {
            case 'dayPeriod':
                var hour = this.components.hour;
                if (hour === null) {
                    return defaultValue !== null && defaultValue !== void 0 ? defaultValue : null;
                }
                return hour < 12 ? 1 : 2;
            default:
                return (_b = (_a = this.components[type]) !== null && _a !== void 0 ? _a : defaultValue) !== null && _b !== void 0 ? _b : null;
        }
    };
    DateComponents.prototype.has = function (components) {
        return (this.filledComponentsMask & components) === components;
    };
    DateComponents.prototype.with = function (type, value) {
        var _a;
        return new DateComponents(__assign(__assign({}, this.components), (_a = {}, _a[type] = value, _a)));
    };
    DateComponents.prototype.toDate = function (defaultDate) {
        var month = this.get('month', defaultDate.getMonth() + 1) - 1;
        var date = new Date();
        date.setFullYear(this.get('year', defaultDate.getFullYear()), month, this.get('day', defaultDate.getDate()));
        date.setHours(this.get('hour', defaultDate.getHours()), this.get('minute', defaultDate.getMinutes()), this.get('second', defaultDate.getSeconds()), 0);
        if (date.getMonth() !== month) {
            date.setDate(0);
        }
        return date;
    };
    DateComponents.prototype.set = function (type, value) {
        if (value === void 0) { value = null; }
        switch (type) {
            case 'dayPeriod':
                if (value !== null) {
                    var hours = this.get('hour', 0);
                    var wasPM = hours >= 12;
                    var isPM = value > 1;
                    if (isPM !== wasPM) {
                        this.components.hour = wasPM ? hours - 12 : hours + 12;
                    }
                    else if (!this.has(DateComponent.hour)) {
                        this.components.hour = 0;
                    }
                    this.invalidateMask('hour', this.get('hour'));
                }
                break;
            default:
                this.components[type] =
                    value === null ? null : clamp(value, MIN_VALUES[type], MAX_VALUES[type]);
        }
        this.invalidateMask(type, this.get(type));
    };
    DateComponents.prototype.invalidateMask = function (type, value) {
        if (value === null) {
            this.filledComponentsMask &= ~DateComponent[type];
        }
        else {
            this.filledComponentsMask |= DateComponent[type];
        }
    };
    return DateComponents;
}());
export { DateComponents };
function isSameDateComponent(type, a, b) {
    return a.get(type) === b.get(type);
}
function isSameYear(a, b) {
    return isSameDateComponent('year', a, b);
}
function isSameMonth(a, b) {
    return isSameYear(a, b) && isSameDateComponent('month', a, b);
}
function isSameDay(a, b) {
    return isSameMonth(a, b) && isSameDateComponent('day', a, b);
}
function isSameHour(a, b) {
    return isSameDay(a, b) && isSameDateComponent('hour', a, b);
}
function isSameMinute(a, b) {
    return isSameHour(a, b) && isSameDateComponent('minute', a, b);
}
function isSameSecond(a, b) {
    return isSameMinute(a, b) && isSameDateComponent('second', a, b);
}
